import {
  Flex,
  Text,
  useColorModeValue,
  Icon,
  HStack,
  Divider,
} from '@chakra-ui/react';
import { 
  LayoutGrid, 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  HelpCircle 
} from 'lucide-react';
import { Metric, MetricGoal } from '../../types';
import { 
  getQuarterWeeks, 
  isMetricOnTrack,
  calculateTrailingStatus
} from '../../utils/scorecardCalculations';

interface ScorecardSummaryStatsProps {
  metrics: Metric[];
  metricGoals: MetricGoal[];
  quarter: number;
  year: number;
}

export function ScorecardSummaryStats({
  metrics,
  metricGoals,
  quarter,
  year,
}: ScorecardSummaryStatsProps) {
  const textColor = useColorModeValue('gray.600', 'gray.400');

  // Calculate stats for the current week
  const stats = (() => {
    // Determine current week number
    const weeks = getQuarterWeeks(quarter, year);
    const currentWeekInfo = weeks.find((w) => w.isReviewWeek) || weeks[weeks.length - 1];
    const currentWeekNumber = currentWeekInfo.weekNumber;

    let onTrack = 0;
    let atRisk = 0;
    let offTrack = 0;
    let noData = 0;

    metrics.forEach((metric) => {
      // Calculate trailing status for each metric
      const weeklyStatuses = weeks.map(week => {
         const goalRecord = metricGoals.find(
            (g) => g.metricId === metric.id && g.periodNumber === week.weekNumber && g.periodType === 'week'
          );
          
          const actual = goalRecord?.actualValue ?? null;
          const target = goalRecord?.goalValue ?? (metric.weeklyGoal || metric.quarterlyGoal || 0);
  
          return isMetricOnTrack(
            actual,
            target,
            metric.goalOrientation || 'gte',
            metric.goalRangeMin,
            metric.goalRangeMax
          ) ?? null;
      });
  
      const status = calculateTrailingStatus(weeklyStatuses, 3);

      if (status === 'green') {
        onTrack++;
      } else if (status === 'yellow') {
        atRisk++;
      } else if (status === 'red') {
        offTrack++;
      } else {
        noData++;
      }
    });

    return {
      total: metrics.length,
      onTrack,
      atRisk,
      offTrack,
      noData,
    };
  })();

  const StatItem = ({ label, count, total, icon, color, bg, borderColor }: any) => {
    const percent = total > 0 ? Math.round((count / total) * 100) : 0;
    
    return (
      <HStack 
        spacing={3} 
        align="center" 
        bg="white" 
        px={3} 
        py={1.5} 
        borderRadius="md" 
        border="1px" 
        borderColor={borderColor || "gray.200"}
        boxShadow="sm"
      >
        <Icon as={icon} color={color} boxSize={4} />
        <Text fontSize="sm" fontWeight="medium" color={textColor}>
          {label} <Text as="span" fontWeight="bold" ml={1}>{count}</Text>
        </Text>
        {label !== 'Total' && (
             <Text fontSize="xs" color="gray.400" ml={1}>{percent}%</Text>
        )}
      </HStack>
    );
  };

  return (
    <HStack spacing={3} wrap="wrap">
      <StatItem 
        label="Total" 
        count={stats.total} 
        total={0}
        icon={LayoutGrid} 
        color="blue.500"
        borderColor="blue.100"
      />
      <StatItem 
        label="On Track" 
        count={stats.onTrack} 
        total={stats.total}
        icon={CheckCircle} 
        color="green.500"
        borderColor="green.100"
      />
       <StatItem 
        label="At Risk" 
        count={stats.atRisk} 
        total={stats.total}
        icon={AlertTriangle} 
        color="orange.400"
        borderColor="orange.100"
      />
      <StatItem 
        label="Off Track" 
        count={stats.offTrack} 
        total={stats.total}
        icon={XCircle} 
        color="red.500"
        borderColor="red.100"
      />
      <StatItem 
        label="No Data" 
        count={stats.noData} 
        total={stats.total}
        icon={HelpCircle} 
        color="gray.400"
        borderColor="gray.200"
      />
    </HStack>
  );
}
